import createHttp from '@/config/http';
import type { ResponseMetaData } from '@/types/service';
import type {
	AllNotificationType,
	CommissionableProduct,
	CreateUserResponse,
	DeleteUserResponse,
	Permissions,
	PermissionsResponse,
	UserPresencePayload,
	UpdateUserResponse,
	User,
	UserProfile,
	UserProfileClinicsResponse,
	UserProfileLocationsResponse,
	UserProfileSalesRepsResponse,
	UsersResponse,
} from '@/types/users';

const http = createHttp(import.meta.env.VITE_USERS_URL);
const USER_PRESENCE_SESSION_KEY = 'hv360-user-presence-session-id';

const createPresenceSessionId = () => {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID();
	}

	return `presence-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const getUserPresenceSessionId = (): string | null => {
	if (typeof window === 'undefined') {
		return null;
	}

	return window.sessionStorage.getItem(USER_PRESENCE_SESSION_KEY);
};

export const ensureUserPresenceSessionId = (): string => {
	const existingSessionId = getUserPresenceSessionId();
	if (existingSessionId) {
		return existingSessionId;
	}

	const nextSessionId = createPresenceSessionId();
	if (typeof window !== 'undefined') {
		window.sessionStorage.setItem(USER_PRESENCE_SESSION_KEY, nextSessionId);
	}

	return nextSessionId;
};

export const getUsers = async (
	page = 1,
	limit = 15,
	columnsFilters = '[]',
	search = '',
	role = '',
	globalFilters = '[]',
	sort = '',
): Promise<{
	items: User[];
	meta: ResponseMetaData;
}> => {
	const response = await http.get<UsersResponse>('/users', {
		params: {
			page,
			limit,
			search,
			role,
			columnsFilters,
			globalFilters,
			sort,
		},
	});
	return {
		items: response.data.data.items,
		meta: response.data.data.metaData,
	};
};

export const getUser = async (type: string, id: string): Promise<User> => {
	const response = await http.get<{ data: User }>(`/users/${type}/${id}`);
	return response.data.data;
};

export const createUser = async (data: User): Promise<CreateUserResponse> => {
	const response = await http.post<CreateUserResponse>('/users', data);
	return response.data;
};

export const updateUser = async (
	id: string,
	data: User,
): Promise<UpdateUserResponse> => {
	const response = await http.put<UpdateUserResponse>(`/users/${id}`, data);
	return response.data;
};

export const deleteUser = async (id: string): Promise<DeleteUserResponse> => {
	const response = await http.delete<DeleteUserResponse>(`/users/${id}`);
	return response.data;
};

export const getAllPermisions = async (
	page = 1,
	limit = 9999,
): Promise<{
	items: Permissions[];
	meta: ResponseMetaData;
}> => {
	const response = await http.get<PermissionsResponse>('/users/permissions', {
		params: {
			page,
			limit,
		},
	});
	return {
		items: response.data.data.items,
		meta: response.data.metaData,
	};
};

export const updateUserProfile = async (
	id: string,
	data: UserProfile,
): Promise<UpdateUserResponse> => {
	const response = await http.patch<UpdateUserResponse>(
		`/users/${id}/profile`,
		data,
	);
	return response.data;
};

export const getUserProlileClinics = async (
	userId = '',
): Promise<UserProfileClinicsResponse> => {
	const response = await http.get<UserProfileClinicsResponse>(
		`/users/${userId}/user-profile-clinics`,
	);
	return response.data;
};

export const getUserProlileLocations = async (
	userId = '',
): Promise<UserProfileLocationsResponse> => {
	const response = await http.get<UserProfileLocationsResponse>(
		`/users/${userId}/user-profile-locations`,
	);
	return response.data;
};

export const getUserProlileSalesReps = async (
	userId = '',
): Promise<UserProfileSalesRepsResponse> => {
	const response = await http.get<UserProfileSalesRepsResponse>(
		`/users/${userId}/user-profile-salesreps`,
	);
	return response.data;
};

export const updateUserProfileClinics = async (
	userId = '',
	data: string[],
): Promise<UpdateUserResponse> => {
	const response = await http.patch<UpdateUserResponse>(
		`/users/${userId}/user-profile-clinics`,
		{ clinicIds: data },
	);
	return response.data;
};

export const updateUserProfileLocations = async (
	userId = '',
	data: string[],
): Promise<UpdateUserResponse> => {
	const response = await http.patch<UpdateUserResponse>(
		`/users/${userId}/user-profile-locations`,
		{ locationIds: data },
	);
	return response.data;
};

export const updateUserProfileSalesReps = async (
	userId = '',
	data: string[],
): Promise<UpdateUserResponse> => {
	const response = await http.patch<UpdateUserResponse>(
		`/users/${userId}/user-profile-salesreps`,
		{ salesRepIds: data },
	);
	return response.data;
};

export const updateMsProducts = async (
	id: string,
	products: CommissionableProduct[],
) => {
	console.log('products', products);
	const response = await http.patch<UpdateUserResponse>(
		`/users/${id}/ms-products`,
		{ products },
	);
	return response.data;
};

export const getAllNotificationTypes = async (): Promise<
	AllNotificationType[]
> => {
	const response = await http.get<{ data: AllNotificationType[] }>(
		'/users/all-notification-types',
	);
	return response.data.data;
};

export const updateQuickActions = async (
	userId: string,
	quickActions: Record<string, string>,
): Promise<UpdateUserResponse> => {
	const response = await http.patch<UpdateUserResponse>(
		`/users/${userId}/quick-actions`,
		{ quickActions },
	);
	return response.data;
};

export const updateUserPresence = async (
	userId: string,
	data: UserPresencePayload,
): Promise<UpdateUserResponse> => {
	const response = await http.patch<UpdateUserResponse>(
		`/users/${userId}/presence`,
		data,
	);
	return response.data;
};
